import { Link, useNavigate } from '@tanstack/react-router';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@nucleus-commerce/ui/components/sidebar';

import { session } from '@/shared/session/session';

/** Sidebar switcher: the organisation name with the current store's currency under it. */
export function StoreSwitcher({ currentStoreId }: { currentStoreId: string | undefined }) {
  const { t } = useTranslation('stores');
  const { me } = session.useSession();
  const navigate = useNavigate();
  const stores = me?.stores ?? [];
  const current = stores.find((s) => s.id === currentStoreId);

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              variant="outline"
              className="data-[state=open]:bg-sidebar-accent h-11 rounded-[10px]"
              data-tour="shell.store-switcher"
            >
              <div className="grid flex-1 text-left text-[13px] leading-tight">
                <span className="truncate font-semibold">
                  {current?.name ?? me?.organization.name ?? '…'}
                </span>
                <span className="text-muted-foreground truncate text-xs">
                  {current
                    ? t('switcher.storeLine', { currency: current.defaultCurrency })
                    : t('switcher.catalog')}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto size-3.5" aria-hidden />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            <DropdownMenuLabel>{t('switcher.label')}</DropdownMenuLabel>
            {stores.map((store) => (
              <DropdownMenuItem
                key={store.id}
                onSelect={() => void navigate({ to: '/s/$storeId', params: { storeId: store.id } })}
              >
                <span className="grid flex-1 leading-tight">
                  <span className="truncate">{store.name}</span>
                  <span className="text-muted-foreground text-xs">
                    {store.defaultCurrency} · {store.timezone}
                  </span>
                </span>
                {store.id === currentStoreId ? (
                  <Check className="ml-auto size-4" aria-hidden />
                ) : null}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/org/stores">{t('switcher.manage')}</Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
